<script setup lang="ts">
/**
 * UNE carte « Ce que permet votre don » (image + titre + description +
 * montant), sortie de `DonateImpactOptions` pour servir à la liste comme au
 * carrousel (plus de 2 paliers, Karim 2026-10-13).
 */
import { getFileUrl } from "~/services/assets";
import type { ImpactOption } from "./DonateImpactOptions.vue";

defineProps<{
  option: ImpactOption;
  selectionne: boolean;
  accent: string;
  formatAmount: (amount: number) => string;
  periodSuffix?: string | null;
}>();
const emit = defineEmits<{ choisir: [] }>();

function imageSrc(image?: string): string | null {
  if (!image) return null;
  if (image.startsWith("http") || image.startsWith("/")) return image;
  return getFileUrl(image, { width: 160, format: "webp", quality: 80 });
}
</script>

<template>
  <!-- min-h-[94px] = hauteur d'une carte AVEC image (72 px + marges) : avec
       ou sans image, toutes les cartes ont les mêmes proportions. -->
  <button
    type="button"
    class="impact-card group relative flex w-full min-h-[94px] items-center gap-3.5 rounded-2xl border bg-white p-2.5 pr-3 text-left"
    :class="selectionne ? '' : 'border-gray-200 hover:border-gray-300'"
    :style="selectionne ? { borderColor: accent, boxShadow: `inset 0 0 0 1px ${accent}` } : undefined"
    :aria-pressed="selectionne"
    @click="emit('choisir')"
  >
    <span v-if="imageSrc(option.image)" class="relative shrink-0">
      <img
        :src="imageSrc(option.image) ?? undefined"
        alt=""
        loading="eager"
        class="h-[72px] w-[72px] rounded-xl object-cover"
      >
      <span
        class="impact-check absolute -left-1.5 -top-1.5 flex size-6 items-center justify-center rounded-full border-2 border-white text-white"
        :class="{ 'impact-check--on': selectionne }"
        :style="{ backgroundColor: accent }"
        aria-hidden="true"
      >
        <UIcon name="i-lucide-check" class="size-3.5" />
      </span>
    </span>
    <!-- Sans image : la même coche, à cheval sur le bord haut de la carte,
         décalée vers l'intérieur pour ne pas être coupée par le bord du
         formulaire (sinon la sélection ne se voyait qu'à la bordure). -->
    <span
      v-else
      class="impact-check absolute left-2.5 -top-2.5 flex size-6 items-center justify-center rounded-full border-2 border-white text-white"
      :class="{ 'impact-check--on': selectionne }"
      :style="{ backgroundColor: accent }"
      aria-hidden="true"
    >
      <UIcon name="i-lucide-check" class="size-3.5" />
    </span>
    <span class="min-w-0 flex-1">
      <span class="block text-[15px] font-semibold leading-snug text-gray-900">{{ option.label }}</span>
      <span
        v-if="option.description"
        class="mt-1 line-clamp-2 block text-[13px] leading-snug text-gray-500"
      >{{ option.description }}</span>
    </span>
    <span
      class="shrink-0 rounded-full px-3.5 py-1.5 text-[15px] font-bold tabular-nums transition-colors duration-200"
      :class="selectionne ? 'text-white' : 'bg-gray-100 text-gray-900 group-hover:bg-gray-200'"
      :style="selectionne ? { backgroundColor: accent } : undefined"
    >
      {{ formatAmount(option.amount) }}<span v-if="periodSuffix" class="font-semibold"> / {{ periodSuffix }}</span>
    </span>
  </button>
</template>

<style scoped>
.impact-card {
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
}

.impact-card:active {
  transform: scale(0.985);
}

.impact-card:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.impact-check {
  opacity: 0;
  transform: scale(0.6);
  transition: opacity 0.2s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.impact-check--on {
  opacity: 1;
  transform: scale(1);
}

@media (prefers-reduced-motion: reduce) {
  .impact-card,
  .impact-check {
    transition: none;
  }
}
</style>
